import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { TenantTypeEnum } from '@zaoblako/shared/modules/saas/enums/tenant.type.enum';
import { TenantStatisticsFindDto, type TenantStatisticsFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-statistics/tenant.statistics.find.dto';
import { TimeZoneEnum } from '@zaoblako/shared/common/enums/time.zone.enum';
import { ContragentFindDto, type ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { SaasDtoKeysEnum } from '@zaoblako/shared/modules/saas/enums/saas-dto-keys.enum';
import { TakenSubdomainDto, type TakenSubdomainInterface } from '@zaoblako/shared/modules/saas/dtos/subdomain/taken-subdomain.dto';
import { TenantSubdomainsDto, type TenantSubdomainsInterface } from '@zaoblako/shared/modules/saas/dtos/subdomain/tenant-subdomains.dto';
import { FrmInstanceFindDto, type FrmInstanceFindInterface } from '@zaoblako/shared/modules/saas/dtos/frm-instance/frm-instance-find.dto';
import type { RatePlanFindInterface } from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan-find.dto';

import * as yup from 'yup';

/** Tenant response body - DTO signature */
export interface TenantFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: string;
  /** All subdomains taken by this tenant - origin {@link TakenSubdomainInterface} objects list */
  takenSubdomains?: TakenSubdomainInterface[];
  /** All subdomains taken by this tenant aggregated by types */
  tenantSubdomains?: TenantSubdomainsInterface;
  /** Subdomain to distinguish between SaaS clients */
  subdomain: string;
  /** Personal account subdomain to distinguish between SaaS clients */
  personalAccountSubdomain: string;
  /** Display name */
  name: string;
  showWelcome?: boolean;
  showDebitWelcome?: boolean;
  innClientRegistrationEnabled?: boolean;
  type?: TenantTypeEnum;
  balance: number;
  /** Tenant creation date and time */
  created: Date;
  /** Tenant last update date and time */
  updated: Date;
  /** Is tenant deleted */
  deleted?: Date;
  timezone: TimeZoneEnum;
  createdBy?: UserFindInterface;
  helpdeskNotificationGroup: UserGroupFindInterface;
  nextPaymentDate: Date;
  ratePlan: RatePlanFindInterface;
  instance: FrmInstanceFindInterface;
  representativeContragent?: ContragentFindInterface;
  statistics?: TenantStatisticsFindInterface;
  /** Tenant's "avatar" (to show on side panel instead of FRM logo) */
  avatar?: FileFindInterface;
}

/** Tenant response body - DTO schema */
export const TenantFindDto = SerdeDtoClass.create<TenantFindInterface>(SaasDtoKeysEnum.TENANT_FIND_DTO, {
  id: {
    label: 'Номер',
    hint: 'UUID',
    serializer: yup.string(),
  },
  takenSubdomains: {
    optional: true,
    label: 'Используемые поддомены (список)',
    nested: TakenSubdomainDto,
    serializer: (_, nested) => yup.array(nested),
  },
  tenantSubdomains: {
    optional: true,
    label: 'Используемые поддомены по типам',
    nested: TenantSubdomainsDto,
    serializer: (_, nested) => nested,
  },
  subdomain: {
    sortable: true,
    label: 'Поддомен для основного режима',
    serializer: yup.string(),
  },
  personalAccountSubdomain: {
    sortable: true,
    label: 'Поддомен для режима ЛК',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'Имя',
    serializer: yup.string(),
  },
  showWelcome: {
    optional: true,
    label: 'Показывать приветствие',
    serializer: yup.boolean(),
  },
  showDebitWelcome: {
    optional: true,
    label: 'Показывать дебетовое приветствие',
    serializer: yup.boolean(),
  },
  innClientRegistrationEnabled: {
    optional: true,
    label: 'Регистрация клиентов по ИНН',
    serializer: yup.boolean(),
  },
  type: {
    optional: true,
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(TenantTypeEnum)),
  },
  balance: {
    label: 'Баланс',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  deleted: {
    sortable: true,
    optional: true,
    label: 'Удалён',
    serializer: yup.date(),
  },
  timezone: {
    label: 'Временная зона',
    serializer: yup
      .string()
      .oneOf(Object.values(TimeZoneEnum)),
  },
  createdBy: {
    optional: true,
    sortable: true,
    label: 'Создатель',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  helpdeskNotificationGroup: {
    label: 'Группа уведомлений HD',
    nested: UserGroupFindDto,
    serializer: (_, nested) => nested,
  },
  nextPaymentDate: {
    label: 'Следующая дата оплаты',
    serializer: yup.date(),
  },
  ratePlan: {
    label: 'Тарифный план',
    nested: SaasDtoKeysEnum.RATE_PLAN_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  instance: {
    sortable: true,
    label: 'Инстанс ФРМ',
    nested: FrmInstanceFindDto,
    serializer: (_, nested) => nested,
  },
  representativeContragent: {
    optional: true,
    label: 'Представитель контрагента',
    nested: ContragentFindDto,
    serializer: (_, nested) => nested,
  },
  statistics: {
    optional: true,
    label: 'Статистика',
    nested: TenantStatisticsFindDto,
    serializer: (_, nested) => nested,
  },
  avatar: {
    optional: true,
    label: 'Аватар',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
});
